import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { payWithRazorpay } from "../lib/razorpay";
import MyPayments from "../components/MyPayments";
import { Briefcase, MapPin, ChevronRight, ArrowLeft, CheckCircle2, Sparkles, Loader2, Target, ShieldCheck, Zap, Users, Phone } from "lucide-react";

const FEATURED_CATEGORIES = [
  { name: "Fashion", emoji: "👗", color: "#FF2A5F", img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=400" },
  { name: "Real Estate", emoji: "🏢", color: "#00E5FF", img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400" },
  { name: "Finance & Loan", emoji: "💰", color: "#FFD700", img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400" },
  { name: "Yoga & Gym", emoji: "🧘", color: "#34D399", img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400" },
  { name: "Beauty & Salon", emoji: "💄", color: "#A855F7", img: "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=400" },
  { name: "Wedding & Events", emoji: "💍", color: "#FF7A00", img: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=400" },
];

const HOW_IT_WORKS = [
  { icon: Target, color: "#FF7A00", title: "Pick your niche", desc: "Choose from 20 business categories that match what you do." },
  { icon: MapPin, color: "#00E5FF", title: "Choose your city", desc: "Select your operating city from 50+ Indian cities for local leads." },
  { icon: Phone, color: "#34D399", title: "Receive verified leads", desc: "Our team contacts genuine prospects and shares their details with you in 24-48 hours." },
];

const BENEFITS = [
  { icon: ShieldCheck, color: "#34D399", title: "100% Verified Leads", desc: "No spam or fake numbers — we verify each prospect before sharing." },
  { icon: Zap, color: "#FFD700", title: "Hot & Ready-to-Convert", desc: "Active buyers, not just casual enquirers — much higher closure rate." },
  { icon: Users, color: "#00E5FF", title: "City-specific Match", desc: "Get leads filtered exactly to your service area & target audience." },
  { icon: Sparkles, color: "#FF2A5F", title: "Free During Launch", desc: "Zero subscription fee right now. Sign up early to lock in benefits." },
];

export default function LeadGen() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [opts, setOpts] = useState({ categories: [], cities: [], cover: {} });
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", email: user?.email || "",
    business_name: "", category: "", city: "", notes: "",
  });
  const [step, setStep] = useState(1); // 1: form, 2: success
  const [busy, setBusy] = useState(false);
  const [payReq, setPayReq] = useState(null);
  const [payRefresh, setPayRefresh] = useState(0);
  const [payMsg, setPayMsg] = useState("");
  const payLead = async (r) => {
    setErr(""); setPayMsg("");
    try {
      const result = await payWithRazorpay({ kind: "lead_gen", lead_request_id: r.id });
      setPayMsg(`✅ ${result}`); setPayReq(p => (p && p.id === r.id ? { ...p, paid: true } : p));
      setMine(m => m.map(x => (x.id === r.id ? { ...x, paid: true } : x)));
      setPayRefresh(k => k + 1);
    } catch (e) { if (e.message !== "Payment cancelled") setErr(e.message); }
  };
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/lead-gen/categories").then(r => setOpts(r.data));
    api.get("/lead-gen/my-requests").then(r => setMine(r.data || []));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/lead-gen/requests", form);
      setStep(2);
      setPayReq(data);
      api.get("/lead-gen/my-requests").then(r => setMine(r.data || []));
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-6 pb-24" data-testid="leadgen-page">
      <Link to="/more" className="flex items-center gap-1 text-xs text-white/60 mb-3" data-testid="leadgen-back"><ArrowLeft className="w-3.5 h-3.5"/>Back</Link>

      <div className="flex items-center gap-2 mb-1">
        <Briefcase className="w-7 h-7 text-[#FF7A00]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Lead Generation</h1>
      </div>
      <p className="text-white/60 text-sm mb-5">Get verified, city-specific business leads delivered directly to you. Built for Indian SMBs &amp; service providers.</p>

      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden mb-5 border border-[#FF7A00]/40" data-testid="leadgen-hero">
        <img src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=800" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF7A00]/40 via-black/55 to-[#FF7A00]/30"/>
        <div className="relative p-5 text-center">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF7A00]/20 border border-[#FF7A00]/40 text-[#FF7A00] text-[10px] font-extrabold uppercase tracking-widest mb-2">
            <Sparkles className="w-3 h-3"/>Free during launch
          </div>
          <h2 className="font-outfit font-extrabold text-2xl leading-tight text-white drop-shadow-lg">Grow your business with<br/><span className="text-[#FF7A00]">verified leads daily</span></h2>
          <p className="text-xs text-white/85 mt-2 max-w-xs mx-auto">Skip cold-calling. Get warm prospects matched to your category &amp; city, ready to buy.</p>
        </div>
      </div>

      {/* HIGHLIGHTED introductory offer — admin-editable via /admin -> Leads -> Config */}
      {opts.cover?.active !== false && (
        <div data-testid="leadgen-intro-offer" className="rounded-3xl mb-5 p-[2px] bg-gradient-to-r from-[#FF7A00] via-[#FF7A00] to-[#FF2A5F] animate-pulse-slow">
          <div className="rounded-3xl bg-[#0B0F19] p-4 text-center">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] text-[10px] font-extrabold uppercase tracking-widest mb-2">
              <Zap className="w-3 h-3"/>Launch Offer · Limited
            </div>
            <div className="font-outfit font-extrabold text-lg text-white leading-tight">{opts.cover?.headline || "First 100 verified leads"} at</div>
            <div className="mt-1 flex items-center justify-center gap-2 flex-wrap">
              <span className="font-extrabold text-2xl text-[#FF7A00]">{(opts.cover?.price_goldly ?? 8300).toLocaleString("en-IN")} Goldly</span>
              <span className="text-white/40 text-sm">or</span>
              <span className="font-extrabold text-2xl text-[#FF7A00]">₹{(opts.cover?.price_inr ?? 1750).toLocaleString("en-IN")}</span>
            </div>
            <p className="text-[10px] text-white/55 mt-1.5">{opts.cover?.subtext || "Hand-picked, verified prospects · Lock the early-bird rate before it's gone."}</p>
          </div>
        </div>
      )}

      {/* How it works */}
      <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2 flex items-center gap-1.5"><Target className="w-3.5 h-3.5"/>How it works</h3>
      <div className="space-y-2 mb-5" data-testid="leadgen-how">
        {HOW_IT_WORKS.map((s, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-2xl border border-white/10 bg-white/5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}22`, color: s.color }}>
              <s.icon className="w-4.5 h-4.5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-extrabold tracking-widest text-white/40">STEP {i + 1}</span>
              </div>
              <div className="font-bold text-sm mt-0.5" style={{ color: s.color }}>{s.title}</div>
              <p className="text-xs text-white/65 mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Featured categories */}
      <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5"/>Popular categories</h3>
      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="leadgen-cats">
        {FEATURED_CATEGORIES.map(c => (
          <button key={c.name} type="button" onClick={() => setForm(f => ({ ...f, category: c.name }))} data-testid={`featured-cat-${c.name.replace(/\W+/g,"-").toLowerCase()}`}
            className={`relative aspect-square rounded-2xl overflow-hidden border active:scale-95 transition ${form.category === c.name ? "ring-2 ring-[#FF7A00]" : ""}`}
            style={{ borderColor: `${c.color}55`, backgroundImage: `url(${c.img})`, backgroundSize: "cover", backgroundPosition: "center" }}>
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 0%, ${c.color}AA 100%)` }}/>
            <div className="absolute bottom-1.5 inset-x-1 text-center">
              <div className="text-xl">{c.emoji}</div>
              <div className="text-[9px] font-extrabold text-white drop-shadow tracking-tight leading-tight">{c.name}</div>
            </div>
          </button>
        ))}
      </div>
      <p className="text-[10px] text-white/45 text-center mb-5">+ 14 more categories available below — choose any one in the form.</p>

      {/* Benefits */}
      <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5"/>Why creators &amp; SMBs love this</h3>
      <div className="grid grid-cols-2 gap-2 mb-6" data-testid="leadgen-benefits">
        {BENEFITS.map((b, i) => (
          <div key={i} className="p-3 rounded-2xl border border-white/10 bg-white/5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-1.5" style={{ background: `${b.color}22`, color: b.color }}>
              <b.icon className="w-4 h-4"/>
            </div>
            <div className="font-bold text-[11px]" style={{ color: b.color }}>{b.title}</div>
            <p className="text-[10px] text-white/60 mt-0.5 leading-snug">{b.desc}</p>
          </div>
        ))}
      </div>

      <div className="mb-3 p-3.5 rounded-2xl bg-gradient-to-br from-[#FF7A00]/15 to-[#FF7A00]/5 border border-[#FF7A00]/40 text-center" data-testid="leadgen-cta-banner">
        <p className="text-sm font-bold text-white">👇 Sign up below in 30 seconds</p>
        <p className="text-[10px] text-white/65 mt-0.5">Our team will message you within 24-48 hours with your first batch of leads.</p>
      </div>

      {step === 2 ? (
        <div className="rounded-3xl bg-gradient-to-br from-emerald-500/15 to-[#FF7A00]/10 border border-emerald-400/40 p-6 text-center" data-testid="leadgen-success">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400 mb-2"/>
          <h2 className="font-outfit font-extrabold text-2xl mb-1">Signed up successfully!</h2>
          <p className="text-sm text-white/70 mb-4">Our team will contact you within 24-48 hours with matching leads for <b style={{color:"#FF7A00"}}>{form.category}</b> in <b style={{color:"#00E5FF"}}>{form.city}</b>.</p>
          <button onClick={() => { setStep(1); setForm({ ...form, category: "", city: "", business_name: "", notes: "" }); }}
            data-testid="leadgen-add-another"
            className="px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm font-bold">+ Add another category/city</button>
          {payReq && !payReq.paid && (
            <button onClick={() => payLead(payReq)} data-testid="leadgen-pay-now"
              className="block w-full mt-3 py-3 rounded-full bg-[#FFD700] text-black font-extrabold">Pay ₹{(opts.cover?.price_inr ?? 1750).toLocaleString("en-IN")} to activate leads</button>
          )}
          {payMsg && <div className="mt-3 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm" data-testid="leadgen-pay-msg">{payMsg}</div>}
          {err && <div className="mt-3 text-[#FF2A5F] text-sm" data-testid="leadgen-pay-err">{err}</div>}
          <Link to="/dashboard" className="block text-xs text-white/60 hover:text-[#00E5FF] mt-3">Or check your Creator Dashboard →</Link>
        </div>
      ) : (
      <form onSubmit={submit} className="space-y-4" data-testid="leadgen-form">
        {/* User info */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-white/60">Your Details</h3>
          <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
            placeholder="Full name" data-testid="lead-name"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
          <input required value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value.replace(/\D/g,"")}))}
            inputMode="numeric" placeholder="Phone (WhatsApp preferred)" data-testid="lead-phone"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
          <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
            placeholder="Email" data-testid="lead-email"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
          <input value={form.business_name} onChange={e => setForm(f => ({...f, business_name: e.target.value}))}
            placeholder="Business name (optional)" data-testid="lead-business"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
        </div>

        {/* Category picker */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#FF7A00]"/>Choose Category</h3>
          <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto" data-testid="lead-cat-picker">
            {opts.categories.map(c => (
              <button key={c} type="button" onClick={() => setForm(f => ({...f, category: c}))}
                data-testid={`lead-cat-${c.replace(/\W+/g,"-").toLowerCase()}`}
                className={`p-2.5 rounded-xl border text-[11px] font-bold text-left transition ${form.category === c
                  ? "bg-[#FF7A00]/15 border-[#FF7A00] text-[#FF7A00]"
                  : "bg-white/5 border-white/10 text-white/80 hover:border-white/30"}`}>
                {c}
              </button>
            ))}
          </div>
          {form.category && <p className="text-[11px] text-emerald-300 mt-2">✓ Selected: <b>{form.category}</b></p>}
        </div>

        {/* City picker */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#00E5FF]"/>Choose City</h3>
          <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto" data-testid="lead-city-picker">
            {opts.cities.map(c => (
              <button key={c} type="button" onClick={() => setForm(f => ({...f, city: c}))}
                data-testid={`lead-city-${c.replace(/\W+/g,"-").toLowerCase()}`}
                className={`p-2 rounded-xl border text-[11px] font-bold text-center transition ${form.city === c
                  ? "bg-[#00E5FF]/15 border-[#00E5FF] text-[#00E5FF]"
                  : "bg-white/5 border-white/10 text-white/80 hover:border-white/30"}`}>
                {c}
              </button>
            ))}
          </div>
          {form.city && <p className="text-[11px] text-emerald-300 mt-2">✓ Selected: <b>{form.city}</b></p>}
        </div>

        {/* Notes */}
        <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
          placeholder="Anything specific we should know? (budget, target audience, urgency etc.)" data-testid="lead-notes"
          className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>

        {err && <div className="text-[#FF2A5F] text-sm" data-testid="lead-err">{err}</div>}

        <button disabled={busy || !form.category || !form.city} data-testid="lead-submit"
          className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF7A00] text-black font-outfit font-extrabold active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Sign up &amp; receive leads <ChevronRight className="w-4 h-4"/></>}
        </button>

        <p className="text-[10px] text-white/40 text-center">Service is free during launch. Once you sign up, our team contacts you with leads filtered to your category &amp; city.</p>
      </form>
      )}

      {/* My past requests */}
      {mine.length > 0 && (
        <div className="mt-6">
          <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">Your Lead Signups</h3>
          <div className="space-y-2" data-testid="my-leads-list">
            {mine.map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-2xl border border-white/10 bg-white/5">
                <div>
                  <div className="text-sm font-bold">{r.category}</div>
                  <div className="text-[11px] text-white/60">{r.city} • {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wider ${
                  r.status === "matched" ? "bg-emerald-500/20 text-emerald-300" :
                  r.status === "closed" ? "bg-white/10 text-white/50" :
                  "bg-[#FF7A00]/15 text-[#FF7A00]"
                }`}>{r.status.toUpperCase()}</span>
                {r.paid ? <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/20 text-emerald-300">PAID</span>
                  : <button onClick={() => payLead(r)} data-testid={`lead-pay-${r.id}`} className="ml-2 px-3 py-1 rounded-full bg-[#FFD700] text-black text-[10px] font-extrabold">PAY</button>}
              </div>
            ))}
          </div>
        </div>
      )}
      <MyPayments kind="lead_gen" refreshKey={payRefresh}/>
    </div>
  );
}
